import * as THREE from 'three';
import {
  type SurfaceBlueprint,
  type SurfaceFloraInstance,
  type LavaPool,
  BIOME_PALETTE,
  BiomeId,
  FloraKind,
  sampleHeightmap,
} from '@viral-space/shared';

/**
 * Renderer for a single planetary surface.
 *
 * Draws three things into a dedicated `THREE.Group` that the
 * `GameScene` mounts when the player lands and unmounts on liftoff:
 *
 *   1. The terrain mesh — a single `THREE.PlaneGeometry` whose
 *      vertices are displaced from the heightmap on first load. One
 *      draw call, ~30k triangles for a 128² heightmap, biome-tinted
 *      vertex colors with a flat-shaded material.
 *   2. Biome sky — a large inverted sphere with the biome's `sky`
 *      colour painted across it. Stars are still rendered by the
 *      existing scene but heavily dimmed by the sky's opacity so
 *      players see the planet sky during day flight and only catch
 *      stars at high altitude.
 *   3. Ambient terrain lighting — a single hemisphere light tuned to
 *      the biome palette (`sun` from above, `ambient` from below) so
 *      the heightmap reads as a real outdoor environment instead of
 *      the dark deep-space lighting used for orbit.
 *
 * Bases, mineable nodes, and NPCs are NOT rendered here — they
 * arrive via the normal `EntityUpdate` stream and the
 * {@link import('../game/entity-renderer.js').EntityRenderer} draws
 * them as Station / Asteroid / NPC entities exactly like in space.
 * This keeps the renderer responsibility-cohesive: SurfaceRenderer
 * owns the static planet, EntityRenderer owns everything dynamic.
 */
export class SurfaceRenderer {
  private group: THREE.Group | null = null;
  private currentSurfaceId: string | null = null;
  private blueprint: SurfaceBlueprint | null = null;
  private hemiLight: THREE.HemisphereLight | null = null;
  private terrainMesh: THREE.Mesh | null = null;
  private skyMesh: THREE.Mesh | null = null;
  private floraMeshes: THREE.InstancedMesh[] = [];
  private mountainMesh: THREE.InstancedMesh | null = null;
  /**
   * Glowing molten discs painted on the heightmap to mark every
   * `lavaPools[]` entry. Visual only — the server's
   * `applySurfaceHazards` does the actual damage check off the
   * same blueprint data.
   */
  private lavaPoolMeshes: THREE.Mesh[] = [];

  constructor(private readonly parent: THREE.Object3D) {}

  /**
   * Mount the heightmap, sky and lighting for the given surface. Idempotent
   * — calling twice for the same surface id is a no-op so the
   * `SurfaceData` message can safely be re-sent during reconnects.
   */
  load(bp: SurfaceBlueprint): void {
    if (this.currentSurfaceId === bp.id) return;
    this.unload();

    const palette = BIOME_PALETTE[bp.biome] ?? BIOME_PALETTE[BiomeId.Dirt];
    const group = new THREE.Group();
    group.name = `surface-${bp.id}`;

    this.skyMesh = this.buildSky(palette.sky);
    group.add(this.skyMesh);

    this.terrainMesh = this.buildTerrain(bp, palette);
    group.add(this.terrainMesh);

    // Battlezone-style mountain wall: a ring of tall triangular
    // peaks placed just inside the rim so the player has a clear
    // visual horizon line. Drawn as a single InstancedMesh so the
    // GPU only pays one draw call for the whole ring even though
    // it visually reads as ~32 distinct mountains.
    this.mountainMesh = this.buildMountainRing(bp, palette);
    if (this.mountainMesh) group.add(this.mountainMesh);

    this.hemiLight = new THREE.HemisphereLight(palette.sun, palette.ambient, 1.4);
    this.hemiLight.position.set(0, bp.maxHeight * 4, 0);
    group.add(this.hemiLight);

    // Static flora — instanced per kind so an entire forest is one
    // draw call. Older blueprints (v1) don't have a `flora` field;
    // just skip in that case.
    const flora = bp.flora ?? [];
    if (flora.length > 0) {
      this.floraMeshes = this.buildFlora(flora, palette);
      for (const m of this.floraMeshes) group.add(m);
    }

    // Lava pool discs — a small, glowing disc per `lavaPools[]`
    // entry. On a Lava biome these decorate the explicit pool
    // markers on top of the already-molten-looking ground; on a
    // Dirt / Rock biome they're the only visible cue that a hazard
    // is there at all, so we render them brighter. Geometry is a
    // tiny CircleGeometry, basic material, no shadows — completely
    // free at the GPU level.
    const lavaPools = bp.lavaPools ?? [];
    if (lavaPools.length > 0) {
      this.lavaPoolMeshes = this.buildLavaPools(bp, lavaPools);
      for (const m of this.lavaPoolMeshes) group.add(m);
    }

    this.parent.add(group);
    this.group = group;
    this.currentSurfaceId = bp.id;
    this.blueprint = bp;
  }

  /**
   * Tear down the active surface. Safe to call multiple times. Frees
   * the heightmap geometry / materials so the GPU isn't holding 64 KB
   * of dormant terrain after a liftoff.
   */
  unload(): void {
    if (!this.group) return;
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const m = obj.material;
        if (Array.isArray(m)) m.forEach((x) => x.dispose());
        else m.dispose();
      }
    });
    this.parent.remove(this.group);
    this.group = null;
    this.terrainMesh = null;
    this.skyMesh = null;
    this.hemiLight = null;
    this.floraMeshes = [];
    this.mountainMesh = null;
    this.lavaPoolMeshes = [];
    this.currentSurfaceId = null;
    this.blueprint = null;
  }

  /**
   * Re-anchor the sky dome to the camera every frame so it follows
   * the player around the surface (the dome is finite — without
   * re-anchoring, players who fly to the edge of the map would see
   * the dome edge).
   */
  update(cameraPos: THREE.Vector3 | null): void {
    if (!this.skyMesh || !cameraPos) return;
    this.skyMesh.position.copy(cameraPos);
  }

  /**
   * Sample the active surface heightmap at world (x, z). Used by the
   * client-side prediction layer so the player ship snaps to the
   * heightmap as soon as input is applied, instead of waiting a full
   * round-trip for the server to clamp it.
   *
   * Returns `null` when no surface is currently loaded.
   */
  sampleHeightAt(x: number, z: number): number | null {
    if (!this.blueprint) return null;
    return sampleHeightmap(this.blueprint, x, z);
  }

  isActive(): boolean {
    return this.blueprint !== null;
  }

  getActiveSurface(): SurfaceBlueprint | null {
    return this.blueprint;
  }

  private buildTerrain(
    bp: SurfaceBlueprint,
    palette: typeof BIOME_PALETTE[BiomeId.Dirt],
  ): THREE.Mesh {
    // Plane segments = heightmapRes - 1. Width/height = surface size.
    const segs = bp.heightmapRes - 1;
    const geo = new THREE.PlaneGeometry(bp.size, bp.size, segs, segs);
    geo.rotateX(-Math.PI / 2); // make the plane face +Y

    const pos = geo.attributes.position as THREE.BufferAttribute;
    const color = new Float32Array(pos.count * 3);
    const ground = new THREE.Color(palette.ground);
    const cliff = new THREE.Color(palette.cliff);

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const y = sampleHeightmap(bp, x, z);
      pos.setY(i, y);

      // Slope-based blend: steeper faces lean toward the cliff color
      // so ridges read as exposed rock and the valleys read as the
      // biome's primary surface color. Approximated from height
      // gradient in screen-space units.
      const dx = sampleHeightmap(bp, x + 4, z) - sampleHeightmap(bp, x - 4, z);
      const dz = sampleHeightmap(bp, x, z + 4) - sampleHeightmap(bp, x, z - 4);
      const slope = Math.min(1, Math.sqrt(dx * dx + dz * dz) / 8);
      const r = ground.r + (cliff.r - ground.r) * slope;
      const g = ground.g + (cliff.g - ground.g) * slope;
      const b = ground.b + (cliff.b - ground.b) * slope;
      color[i * 3] = r;
      color[i * 3 + 1] = g;
      color[i * 3 + 2] = b;
    }
    pos.needsUpdate = true;
    geo.setAttribute('color', new THREE.BufferAttribute(color, 3));
    geo.computeVertexNormals();

    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      flatShading: true,
      roughness: 0.95,
      metalness: 0.04,
    });
    const mesh = new THREE.Mesh(geo, material);
    mesh.name = 'surface-terrain';
    // Terrain doesn't move; opt out of frustum culling early so we
    // don't pay the per-frame cost of recomputing the bounding box
    // for a static 128² mesh. The surface is contained in a known
    // square so the renderer naturally clips it.
    mesh.frustumCulled = false;
    return mesh;
  }

  /**
   * Build one `THREE.InstancedMesh` per `FloraKind` present in the
   * blueprint. All instances of a kind share the same geometry +
   * material, so a forest of 80 trees costs the GPU one draw call
   * instead of 80. Templates are intentionally low-poly (cone +
   * trunk for trees, octahedron for crystals) — Battlezone's
   * silhouettes carry the look better than smooth high-poly art.
   */
  private buildFlora(
    flora: SurfaceFloraInstance[],
    palette: typeof BIOME_PALETTE[BiomeId.Dirt],
  ): THREE.InstancedMesh[] {
    const grouped = new Map<FloraKind, SurfaceFloraInstance[]>();
    for (const f of flora) {
      const arr = grouped.get(f.kind) ?? [];
      arr.push(f);
      grouped.set(f.kind, arr);
    }

    const out: THREE.InstancedMesh[] = [];
    const dummy = new THREE.Object3D();
    for (const [kind, instances] of grouped) {
      const tpl = this.floraTemplate(kind, palette);
      const mesh = new THREE.InstancedMesh(tpl.geometry, tpl.material, instances.length);
      mesh.name = `surface-flora-${kind}`;
      mesh.frustumCulled = false; // group is local to surface; one cull box at the parent is fine
      for (let i = 0; i < instances.length; i++) {
        const inst = instances[i]!;
        dummy.position.set(inst.position.x, inst.position.y, inst.position.z);
        dummy.rotation.set(0, inst.rotY, 0);
        dummy.scale.setScalar(inst.scale * tpl.scaleBias);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
      out.push(mesh);
    }
    return out;
  }

  /**
   * Return a single low-poly mesh template per flora kind. The
   * geometry is *merged* per kind so the InstancedMesh can share one
   * VAO across all instances. Geometry origin sits at ground level
   * so an instance placed at heightmap-Y plants the prop on the
   * ground without an offset hack.
   *
   * Sizes are tuned so a tree (the dominant flora kind on Grass /
   * Dirt biomes) reads at ~25 u tall — taller than a Fighter (3 u)
   * by an order of magnitude, comparable to a Spaceport tower (17 u),
   * which puts it in the Battlezone "actual landmark" silhouette
   * bracket instead of the original "ankle-high decoration" bracket
   * the user reported as "trees are too small".
   */
  private floraTemplate(
    kind: FloraKind,
    palette: typeof BIOME_PALETTE[BiomeId.Dirt],
  ): { geometry: THREE.BufferGeometry; material: THREE.Material; scaleBias: number } {
    const ground = new THREE.Color(palette.ground);
    const cliff = new THREE.Color(palette.cliff);
    const sun = new THREE.Color(palette.sun);

    switch (kind) {
      case FloraKind.Tree: {
        // Battlezone-sized canopy tree — sized to read as a true
        // landmark on the horizon and not a "lawn ornament". The
        // base mesh is ~30 u tall (trunk 12 u + canopies stacked to
        // ~30 u) and gets multiplied by `scaleBias * instance.scale`
        // in the renderer (~1.4 × ~2.0 = ~2.8x), so the typical
        // in-world tree silhouette ends up ~80–90 u tall — a
        // dominating wall a Fighter (~3 u tall) flies UNDER. The
        // collider radius (FLORA_COLLIDE_BASE_RADIUS[Tree] = 3.0 in
        // shared, scaled by the same factor) gives a ~12 u trunk
        // collision cylinder so the player physically can't punch
        // through the trunk in either prediction or server world.
        const trunk = new THREE.CylinderGeometry(1.4, 2.0, 12, 6);
        trunk.translate(0, 6, 0);
        const lowerCanopy = new THREE.ConeGeometry(7.5, 13, 8);
        lowerCanopy.translate(0, 14, 0);
        const midCanopy = new THREE.ConeGeometry(5.5, 11, 8);
        midCanopy.translate(0, 21, 0);
        const upperCanopy = new THREE.ConeGeometry(3.6, 9, 8);
        upperCanopy.translate(0, 27, 0);
        const merged = mergeGeoms([
          { geo: trunk, color: new THREE.Color(0x3a2614) },
          { geo: lowerCanopy, color: new THREE.Color(0x2f6b2a).lerp(cliff, 0.15) },
          { geo: midCanopy, color: new THREE.Color(0x336f2e).lerp(cliff, 0.12) },
          { geo: upperCanopy, color: new THREE.Color(0x376f30).lerp(cliff, 0.1) },
        ]);
        return {
          geometry: merged,
          material: floraMaterial(),
          scaleBias: 1.4,
        };
      }
      case FloraKind.Shrub: {
        // Shrubs stay ankle-height by design — they're filler
        // around the bigger canopy trees, not landmarks. Bumped
        // slightly so they're visible from the chase camera but
        // intentionally don't compete with trees for attention.
        const ball = new THREE.IcosahedronGeometry(2.4, 0);
        ball.translate(0, 2.0, 0);
        const merged = mergeGeoms([
          { geo: ball, color: new THREE.Color(0x395a2c).lerp(ground, 0.2) },
        ]);
        return { geometry: merged, material: floraMaterial(), scaleBias: 1.2 };
      }
      case FloraKind.Mushroom: {
        // Giant alien mushrooms — taller stem + wider cap so they
        // dominate the horizon. ~14 u base mesh × ~2.5x runtime
        // scale ≈ ~35 u tall in-world. Cap radius (3.4 base
        // collider × bias × scale) gives a ~14 u collision disc
        // — pilots have to fly around them, not under them.
        const stem = new THREE.CylinderGeometry(1.4, 2.0, 7, 6);
        stem.translate(0, 3.5, 0);
        const cap = new THREE.SphereGeometry(4.6, 12, 7, 0, Math.PI * 2, 0, Math.PI / 2);
        cap.translate(0, 7.0, 0);
        const merged = mergeGeoms([
          { geo: stem, color: new THREE.Color(0xeae0c2) },
          { geo: cap, color: new THREE.Color(0xbf3344) },
        ]);
        return { geometry: merged, material: floraMaterial(), scaleBias: 1.6 };
      }
      case FloraKind.Crystal: {
        // Crystal cluster — ~6 u tall main shard that glows under
        // the surface's HemisphereLight. The smaller satellite
        // shards add silhouette interest at distance.
        const a = new THREE.OctahedronGeometry(2.4);
        a.translate(0, 2.4, 0);
        const b = new THREE.OctahedronGeometry(1.4);
        b.translate(1.8, 1.4, 0.8);
        const c = new THREE.OctahedronGeometry(1.2);
        c.translate(-1.2, 1.2, 1.0);
        const tint = sun.clone().lerp(new THREE.Color(0xaaccff), 0.6);
        const merged = mergeGeoms([
          { geo: a, color: tint },
          { geo: b, color: tint },
          { geo: c, color: tint },
        ]);
        return { geometry: merged, material: floraMaterial(true), scaleBias: 1.4 };
      }
      case FloraKind.Boulder: {
        // Bigger, more imposing boulders so they read as actual
        // cover instead of pebbles. Two-rock cluster gives variety
        // across instances even though every boulder shares the
        // same template.
        const r1 = new THREE.DodecahedronGeometry(2.6, 0);
        r1.translate(0, 2.2, 0);
        const r2 = new THREE.DodecahedronGeometry(1.6, 0);
        r2.translate(2.2, 1.2, 0.6);
        const merged = mergeGeoms([
          { geo: r1, color: cliff.clone().lerp(ground, 0.3) },
          { geo: r2, color: cliff.clone().lerp(ground, 0.3) },
        ]);
        return { geometry: merged, material: floraMaterial(), scaleBias: 1.3 };
      }
      case FloraKind.LavaShard: {
        // Volcanic obsidian shards — taller and more dramatic so
        // lava biomes read as hostile, jagged terrain. ~8 u tall.
        const a = new THREE.ConeGeometry(1.4, 6, 5);
        a.translate(0, 3.0, 0);
        const b = new THREE.ConeGeometry(0.9, 4, 5);
        b.translate(1.4, 2.0, 0.7);
        const merged = mergeGeoms([
          { geo: a, color: new THREE.Color(0x1a0a0a) },
          { geo: b, color: new THREE.Color(0x401414) },
        ]);
        return { geometry: merged, material: floraMaterial(true), scaleBias: 1.5 };
      }
      case FloraKind.IcePillar: {
        // Tall ice spires — the signature ice-biome silhouette.
        // ~16 u base mesh × ~3x runtime scale ≈ 50 u tall in-world.
        // Bigger and visibly translucent so they sell the biome.
        const p = new THREE.ConeGeometry(2.4, 16, 6);
        p.translate(0, 8.0, 0);
        const merged = mergeGeoms([
          { geo: p, color: new THREE.Color(0xd4ecff) },
        ]);
        return { geometry: merged, material: floraMaterial(true), scaleBias: 1.5 };
      }
      case FloraKind.AlienBulb: {
        // Bioluminescent alien bulb on a thick stalk. Stalk reads
        // as plant matter, the bulb is brightly emissive. ~10 u
        // base mesh × ~2.2× runtime scale ≈ 22 u tall in-world —
        // tall enough to be navigated around but never dominates
        // the horizon like a tree does. Designed to add an "exotic
        // biome" silhouette to Grass / Dirt worlds without making
        // them feel like Earth.
        const stalk = new THREE.CylinderGeometry(0.9, 1.4, 7, 6);
        stalk.translate(0, 3.5, 0);
        const bulb = new THREE.SphereGeometry(2.6, 10, 8);
        bulb.translate(0, 8.5, 0);
        const tendrilA = new THREE.ConeGeometry(0.5, 3.0, 5);
        tendrilA.translate(1.6, 9.5, 0);
        tendrilA.rotateZ(-0.3);
        const tendrilB = new THREE.ConeGeometry(0.5, 3.0, 5);
        tendrilB.translate(-1.6, 9.5, 0);
        tendrilB.rotateZ(0.3);
        const merged = mergeGeoms([
          { geo: stalk, color: new THREE.Color(0x2a4628) },
          { geo: bulb, color: new THREE.Color(0x88ffaa).lerp(sun, 0.3) },
          { geo: tendrilA, color: new THREE.Color(0x66dd99) },
          { geo: tendrilB, color: new THREE.Color(0x66dd99) },
        ]);
        return { geometry: merged, material: floraMaterial(true), scaleBias: 1.5 };
      }
      case FloraKind.TwistedSpire: {
        // Helical basalt / glass spike — a stack of progressively
        // smaller, slightly rotated cones gives the corkscrew look
        // without an actual swept geometry. ~16 u base mesh × ~2×
        // runtime scale ≈ 32 u tall. Reads as a hostile, alien
        // monolith on Rock and Lava biomes.
        const tint = cliff.clone().lerp(new THREE.Color(0x1a1418), 0.4);
        const parts: { geo: THREE.BufferGeometry; color: THREE.Color }[] = [];
        const segs = 5;
        let yOff = 0;
        for (let i = 0; i < segs; i++) {
          const r0 = 2.2 - i * 0.35;
          const r1 = 1.7 - i * 0.30;
          const h = 4.2 - i * 0.4;
          if (r0 <= 0.2 || h <= 0.2) break;
          const cone = new THREE.ConeGeometry(r0, h, 5);
          cone.translate(0, yOff + h / 2, 0);
          // Slight per-tier yaw so the silhouette twists.
          cone.rotateY((i * Math.PI) / 4);
          parts.push({ geo: cone, color: tint });
          yOff += h * 0.85;
        }
        const merged = mergeGeoms(parts);
        return { geometry: merged, material: floraMaterial(), scaleBias: 1.5 };
      }
      case FloraKind.JellyFlora: {
        // Floating jellyfish-like dome anchored by a thin tether to
        // the ground. The cap is a hemisphere; the tether is a
        // narrow cylinder. ~10 u base mesh × ~2× runtime scale ≈
        // 20 u tall, with the dome itself sitting ~12–16 u above
        // the ground for the "drifting in mid-air" silhouette.
        const tether = new THREE.CylinderGeometry(0.25, 0.35, 6, 5);
        tether.translate(0, 3.0, 0);
        const cap = new THREE.SphereGeometry(2.8, 12, 6, 0, Math.PI * 2, 0, Math.PI * 0.55);
        cap.translate(0, 7.5, 0);
        const trailA = new THREE.CylinderGeometry(0.18, 0.04, 4, 5);
        trailA.translate(1.0, 5.0, 0);
        const trailB = new THREE.CylinderGeometry(0.18, 0.04, 4, 5);
        trailB.translate(-1.0, 5.0, 0);
        const merged = mergeGeoms([
          { geo: tether, color: new THREE.Color(0x88c0ff) },
          { geo: cap, color: new THREE.Color(0xaaccff).lerp(sun, 0.2) },
          { geo: trailA, color: new THREE.Color(0xbbdcff) },
          { geo: trailB, color: new THREE.Color(0xbbdcff) },
        ]);
        return { geometry: merged, material: floraMaterial(true), scaleBias: 1.4 };
      }
    }
  }

  /**
   * Build the perimeter mountain ring — the visible "Battlezone
   * wall" silhouette. We deliberately stack this on TOP of the
   * heightmap rim instead of replacing it: the heightmap rim still
   * does the actual collision (ships clamp into it via the gravity
   * branch) and these decorative peaks only have to read as a
   * boundary line at distance.
   *
   * Returns `null` for blueprints whose size is implausibly small
   * (defensive against test-only fixtures).
   */
  private buildMountainRing(
    bp: SurfaceBlueprint,
    palette: typeof BIOME_PALETTE[BiomeId.Dirt],
  ): THREE.InstancedMesh | null {
    const half = bp.size / 2;
    if (half < 50) return null;

    // Place peaks at the *outer* end of the rim ramp so they sit on
    // top of the tallest part of the heightmap instead of poking up
    // out of flat ground. Matches the new fbmHeightmap rim window
    // (radial 0.78 → 0.92) — anchor the cones at radial 0.93 so
    // their bases just clear the ramp and their tips form a clean
    // horizon line that reads as "world wall".
    const ringR = half * 0.93;

    // 36 peaks gives a smooth Battlezone-style horizon at default
    // surface size (1600 u → ~278 u between peaks). One InstancedMesh
    // = one draw call for the whole ring.
    const PEAK_COUNT = 36;
    const PEAK_HEIGHT = bp.maxHeight * 1.4;
    const PEAK_BASE_RADIUS = (Math.PI * 2 * ringR) / PEAK_COUNT * 0.7;

    const cone = new THREE.ConeGeometry(PEAK_BASE_RADIUS, PEAK_HEIGHT, 5, 1, false);
    cone.translate(0, PEAK_HEIGHT / 2, 0);
    const cliff = new THREE.Color(palette.cliff);
    const ground = new THREE.Color(palette.ground);
    // Vertex-color the cone so the apex picks up the sun-lit cliff
    // tone and the base blends into the ground. Looks like real
    // sun-lit rock without a per-instance shader pass.
    const colors = new Float32Array(cone.attributes.position.count * 3);
    const pos = cone.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const y = pos.getY(i);
      const t = Math.min(1, Math.max(0, y / PEAK_HEIGHT));
      const c = ground.clone().lerp(cliff, 1 - t * 0.7);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    cone.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    cone.computeVertexNormals();

    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      flatShading: true,
      roughness: 1.0,
      metalness: 0.0,
    });
    const mesh = new THREE.InstancedMesh(cone, mat, PEAK_COUNT);
    mesh.name = 'surface-mountain-ring';
    mesh.frustumCulled = false;

    // Deterministic placement seeded off the blueprint id so two
    // clients on the same surface see the same horizon. Don't pull
    // SeededRandom in here — a simple sin-hash on the seed gives a
    // stable, well-distributed jitter for ~30 peaks.
    const dummy = new THREE.Object3D();
    const baseSeed = bp.seed;
    for (let i = 0; i < PEAK_COUNT; i++) {
      // Per-peak deterministic jitter so the ring doesn't read as
      // 36 identical cones. Each axis uses a different salt so the
      // jitters are uncorrelated.
      const a = (i / PEAK_COUNT) * Math.PI * 2;
      const aJitter = (Math.sin((i + 1) * 12.93 + baseSeed * 0.1) * 0.5) * (Math.PI * 2 / PEAK_COUNT) * 0.4;
      const rJitter = (Math.sin((i + 1) * 7.31 + baseSeed * 0.27) * 0.5 + 0.5) * (half * 0.06);
      const heightJitter = 0.6 + Math.abs(Math.sin((i + 1) * 5.09 + baseSeed * 0.71)) * 0.7;
      const widthJitter = 0.85 + Math.abs(Math.sin((i + 1) * 3.17 + baseSeed * 0.41)) * 0.4;
      const rotJitter = Math.sin((i + 1) * 2.44 + baseSeed * 0.19) * Math.PI;

      const px = Math.cos(a + aJitter) * (ringR + rJitter);
      const pz = Math.sin(a + aJitter) * (ringR + rJitter);
      const py = sampleHeightmap(bp, px, pz);
      dummy.position.set(px, py, pz);
      dummy.rotation.set(0, rotJitter, 0);
      // Non-uniform scale: wider at base, taller at tip, so adjacent
      // peaks read as a ridge rather than a fence of clones.
      dummy.scale.set(widthJitter, heightJitter, widthJitter);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    return mesh;
  }

  /**
   * Build a flat glowing disc per lava pool entry, lifted a hair
   * above the heightmap so the molten surface reads as a layer on
   * top of the ground rather than the ground itself. Pool radius
   * comes from the blueprint (matches the server's damage circle
   * exactly, so what the player sees is what burns them).
   */
  private buildLavaPools(bp: SurfaceBlueprint, pools: LavaPool[]): THREE.Mesh[] {
    const out: THREE.Mesh[] = [];
    const liftAboveGround = 0.4;
    for (const p of pools) {
      const geo = new THREE.CircleGeometry(p.radius, 24);
      geo.rotateX(-Math.PI / 2);
      const mat = new THREE.MeshBasicMaterial({
        color: 0xff5a14,
        transparent: true,
        opacity: 0.85,
        // Lava reads through dust / fog so leave depth-write on
        // but let the renderer still respect existing depth — the
        // disc is at ground level so anything in front of it
        // (rocks, ships, NPCs) still occludes correctly.
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(
        p.position.x,
        sampleHeightmap(bp, p.position.x, p.position.z) + liftAboveGround,
        p.position.z,
      );
      mesh.name = 'surface-lava-pool';
      mesh.frustumCulled = false;
      // Render after the terrain pass so the glow always wins the
      // z-fight at the disc edge, but before flora / entities so
      // a tree growing on the pool rim still shows in front.
      mesh.renderOrder = -10;
      out.push(mesh);
    }
    return out;
  }

  private buildSky(skyColor: number): THREE.Mesh {
    // Inverted sphere ~ 5x larger than the surface so a player at the
    // edge still sees a believable horizon. Drawn with `BackSide` so
    // the inside of the sphere is what's lit, and `depthWrite: false`
    // so it never occludes scene content.
    const geo = new THREE.SphereGeometry(8000, 24, 16);
    const mat = new THREE.MeshBasicMaterial({
      color: skyColor,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = 'surface-sky';
    mesh.renderOrder = -900; // behind everything except orbital starfield
    return mesh;
  }
}

/**
 * Merge an array of `{ geo, color }` pairs into a single
 * `BufferGeometry` with a per-vertex `color` attribute. Used by the
 * flora templates so an InstancedMesh can be drawn with one material
 * (vertex-coloured) yet still have multi-coloured props (trunk vs
 * canopy, stem vs cap, …). Cheap because the input geometries are
 * tiny (a tree is two primitives).
 */
function mergeGeoms(parts: { geo: THREE.BufferGeometry; color: THREE.Color }[]): THREE.BufferGeometry {
  let totalVerts = 0;
  let totalIndex = 0;
  for (const p of parts) {
    totalVerts += (p.geo.attributes.position as THREE.BufferAttribute).count;
    if (p.geo.index) totalIndex += p.geo.index.count;
  }
  const positions = new Float32Array(totalVerts * 3);
  const normals = new Float32Array(totalVerts * 3);
  const colors = new Float32Array(totalVerts * 3);
  const indices = totalIndex > 0 ? new Uint32Array(totalIndex) : null;

  let vOff = 0;
  let iOff = 0;
  for (const p of parts) {
    p.geo.computeVertexNormals();
    const pos = p.geo.attributes.position as THREE.BufferAttribute;
    const nrm = p.geo.attributes.normal as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      positions[(vOff + i) * 3] = pos.getX(i);
      positions[(vOff + i) * 3 + 1] = pos.getY(i);
      positions[(vOff + i) * 3 + 2] = pos.getZ(i);
      normals[(vOff + i) * 3] = nrm.getX(i);
      normals[(vOff + i) * 3 + 1] = nrm.getY(i);
      normals[(vOff + i) * 3 + 2] = nrm.getZ(i);
      colors[(vOff + i) * 3] = p.color.r;
      colors[(vOff + i) * 3 + 1] = p.color.g;
      colors[(vOff + i) * 3 + 2] = p.color.b;
    }
    if (indices && p.geo.index) {
      const src = p.geo.index;
      for (let i = 0; i < src.count; i++) {
        indices[iOff + i] = src.getX(i) + vOff;
      }
      iOff += src.count;
    }
    vOff += pos.count;
    p.geo.dispose();
  }

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  out.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  if (indices) out.setIndex(new THREE.BufferAttribute(indices, 1));
  return out;
}

/**
 * Standard-material factory shared by every flora kind. Vertex colours
 * are baked at template time (so trunk/canopy show up in different
 * colours), `flatShading` matches the heightmap material, and the
 * optional `glow` flag boosts emissive for crystals / lava shards
 * so they're recognisable at distance under low light.
 */
function floraMaterial(glow = false): THREE.Material {
  return new THREE.MeshStandardMaterial({
    vertexColors: true,
    flatShading: true,
    metalness: glow ? 0.3 : 0.1,
    roughness: glow ? 0.4 : 0.85,
    emissive: glow ? 0xffffff : 0x000000,
    emissiveIntensity: glow ? 0.18 : 0,
  });
}
